import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ReviewStoreV2, ReviewStateV2, INITIAL_REVIEW_STATE } from '../review-store-v2.service';
import { ToastBarService } from '../../../../../services/toast-bar.service';

import { ReviewVersionBarV2Component } from '../review-version-bar-v2/review-version-bar-v2.component';
import { ReviewDecisionsV2Component } from '../review-decisions-v2/review-decisions-v2.component';
import { ReviewTimelineV2Component } from '../review-timeline-v2/review-timeline-v2.component';
import { ReviewCommentCardV2Component } from '../review-comment-card-v2/review-comment-card-v2.component';

import {
  TemplateVersionDecision,
  TemplateVersionDecisionType,
} from '../../../../../models/template-version/template-version.interface';
import { INotification } from '../../../../../models/template/notifications-model.interface';

import {
  CommentGroup,
  TimelineEvent,
  decisionType,
  commentCountLabel,
  groupComments,
  commentNumbers,
  buildTimeline,
} from '../review-utils';

@Component({
  selector: 'app-review-panel-v2',
  imports: [
    CommonModule,
    FormsModule,
    ReviewVersionBarV2Component,
    ReviewDecisionsV2Component,
    ReviewTimelineV2Component,
    ReviewCommentCardV2Component,
  ],
  templateUrl: './review-panel-v2.component.html',
  styleUrl: './review-panel-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewPanelV2Component implements OnInit, OnDestroy {
  @Input() compact = false;
  @Input() currentUsername: string | null = null;
  @Input() languageLabel = 'EN';
  @Input() canRestore = false;
  @Input() selectedBlockId: string | null = null;

  @Output() newVersion = new EventEmitter<void>();
  @Output() restoreVersion = new EventEmitter<number>();

  state: ReviewStateV2 = INITIAL_REVIEW_STATE;
  groups: CommentGroup[] = [];
  numbers = new Map<string, number>();
  timeline: TimelineEvent[] = [];
  viewingDecisions: TemplateVersionDecision[] = [];
  isOlder = false;
  tab: 'activity' | 'timeline' = 'activity';
  composerText = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: ReviewStoreV2,
    private toast: ToastBarService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.store.state$.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.state = s;
      const all = groupComments(this.state.versions, this.state.comments);
      this.groups = this.compact ? all.filter((g) => g.comments.length > 0) : all;
      this.numbers = commentNumbers(this.state.comments);
      this.timeline = buildTimeline(this.state.versions, this.state.comments);
      this.viewingDecisions =
        this.state.versions.find((v) => v.version === this.state.viewing)?.decisions ?? [];
      this.isOlder =
        this.state.latest !== null &&
        this.state.viewing !== null &&
        this.state.viewing !== this.state.latest;
      this.cdr.markForCheck();
    });

    this.store.focusComment$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      this.tab = 'activity';
      this.cdr.markForCheck();
      requestAnimationFrame(() => {
        this.host.nativeElement.querySelector(
          `[data-comment-id="${CSS.escape(id)}"]`
        )?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  countLabel(g: CommentGroup): string {
    return commentCountLabel(g);
  }

  decisionTypeOf(d: TemplateVersionDecision): { key: TemplateVersionDecisionType; label: string; tone: 'green' | 'amber' | 'red' | 'grey' } {
    return decisionType(d.type);
  }

  decisionAuthor(d: TemplateVersionDecision): string {
    return d.createdBy?.name || d.createdBy?.username || '';
  }

  newestFirst(decisions: TemplateVersionDecision[] | null | undefined): TemplateVersionDecision[] {
    return [...(decisions ?? [])].reverse();
  }

  trackComment(_: number, c: INotification): string {
    return c.notificationId!;
  }

  trackGroup(_: number, g: CommentGroup): string {
    return g.version ? String(g.version.version) : 'earlier';
  }

  onSelectVersion(n: number): void {
    this.store.selectVersion(n);
  }

  goToLatest(): void {
    if (this.state.latest !== null) {
      this.store.selectVersion(this.state.latest);
    }
  }

  onRestore(): void {
    if (this.state.viewing !== null && this.canRestore) {
      this.restoreVersion.emit(this.state.viewing);
    }
  }

  onSubmitDecision(e: { type: TemplateVersionDecisionType; comment: string | null }): void {
    this.store.addDecision(e.type, e.comment).subscribe({
      error: () => {
        this.toast.showToastError('Could not save the decision.');
      },
    });
  }

  submitComposer(): void {
    const text = this.composerText.trim();
    if (!text) return;

    if (this.state.latest === null) {
      this.toast.showToast({
        severity: 'info',
        summary: 'Info',
        detail: 'Pull a version into review first (New version).',
      });
      return;
    }

    const blocks = this.state.viewingBlocks ?? [];
    const pin = this.state.composerPin;
    const blockId =
      pin?.blockId ??
      (this.selectedBlockId && blocks.some((b) => b.blockId === this.selectedBlockId)
        ? this.selectedBlockId
        : null) ??
      blocks.find((b) => b.name !== 'subject' && b.name !== 'preheader')?.blockId ??
      blocks[0]?.blockId;

    if (!blockId) {
      this.toast.showToastError('Could not save the comment.');
      return;
    }

    this.store.addComment(text, blockId, pin ? { x: pin.x, y: pin.y } : null).subscribe({
      next: () => {
        this.composerText = '';
        this.cdr.markForCheck();
      },
      error: () => {
        this.toast.showToastError('Could not save the comment.');
      },
    });
  }

  cancelComposer(): void {
    this.store.setComposerOpen(false);
    this.composerText = '';
  }

  openComposer(): void {
    this.store.setComposerOpen(true);
  }

  removeComposerPin(): void {
    this.store.clearComposerPin();
  }

  onToggleDone(c: INotification): void {
    this.store.toggleDone(c).subscribe({
      error: () => {
        this.toast.showToastError('Could not update the comment.');
      },
    });
  }

  onSaveEdit(c: INotification, text: string): void {
    this.store.editComment(c, text).subscribe({
      error: () => {
        this.toast.showToastError('Could not update the comment.');
      },
    });
  }

  onDelete(c: INotification): void {
    this.store.deleteComment(c).subscribe({
      error: () => {
        this.toast.showToastError('Could not update the comment.');
      },
    });
  }

  onAddReply(c: INotification, text: string): void {
    this.store.addReply(c, text).subscribe({
      error: () => {
        this.toast.showToastError('Could not update the comment.');
      },
    });
  }

  onSaveReply(c: INotification, e: { replyId: string; text: string }): void {
    this.store.editReply(c, e.replyId, e.text).subscribe({
      error: () => {
        this.toast.showToastError('Could not update the comment.');
      },
    });
  }

  onAnnotate(c: INotification): void {
    if (c.version == null) return;
    this.store.setMode('annotate');
    if (this.store.snapshot.viewing !== c.version) {
      this.store.selectVersion(c.version);
    }
    this.store.startPinMove(c.notificationId!);
    this.toast.showToast({
      severity: 'info',
      summary: 'Info',
      detail: 'Click preview to place annotation',
    });
  }

  onShowAnnotation(c: INotification): void {
    if (c.version != null && c.version !== this.state.viewing) {
      this.store.selectVersion(c.version);
    }
    this.store.focusComment(c.notificationId!);
  }
}
